/**
 * Utilities for calculating WCAG 2.0 contrast ratios between colors.
 * Based on the Web Content Accessibility Guidelines (WCAG) 2.0.
 * @see https://www.w3.org/TR/WCAG20/#contrast-ratiodef
 */

export interface RGB {
    r: number; // 0-255
    g: number; // 0-255
    b: number; // 0-255
}

export interface RGBA extends RGB {
    a: number; // 0-1
}

/**
 * Converts an 8-bit RGB color component (0-255) to a normalized value (0-1)
 * and applies gamma correction for sRGB color space.
 *
 * @param component - RGB color component value (0-255)
 * @returns Gamma-corrected component value (0-1)
 */
function gammaCorrect(component: number): number {
    const normalized = component / 255;
    if (normalized <= 0.03928) {
        return normalized / 12.92;
    }
    return Math.pow((normalized + 0.055) / 1.055, 2.4);
}

/**
 * Calculates the relative luminance of a color according to WCAG 2.0.
 * Luminance is the measure of brightness of a color.
 *
 * @param rgb - RGB color object with r, g, b values (0-255)
 * @returns Relative luminance value (0-1)
 */
export function calculateLuminance(rgb: RGB): number {
    const r = gammaCorrect(rgb.r);
    const g = gammaCorrect(rgb.g);
    const b = gammaCorrect(rgb.b);

    // WCAG luminance formula: 0.2126 * R + 0.7152 * G + 0.0722 * B
    return 0.2126 * r + 0.7152 * g + 0.0722 * b;
}

/**
 * Calculates the contrast ratio between two colors according to WCAG 2.0.
 * The contrast ratio is defined as (L1 + 0.05) / (L2 + 0.05), where L1 is the
 * relative luminance of the lighter color and L2 is the relative luminance of
 * the darker color.
 *
 * @param color1 - First RGB color
 * @param color2 - Second RGB color
 * @returns Contrast ratio (1-21)
 */
export function calculateContrastRatio(color1: RGB, color2: RGB): number {
    const luminance1 = calculateLuminance(color1);
    const luminance2 = calculateLuminance(color2);

    const lighter = Math.max(luminance1, luminance2);
    const darker = Math.min(luminance1, luminance2);

    return (lighter + 0.05) / (darker + 0.05);
}

/**
 * Converts a hex color string to RGB.
 *
 * @param hex - Hex color string (e.g., '#FF5733', '#F57', 'FF5733')
 * @returns RGB color object
 * @throws Error if hex string is invalid
 */
export function hexToRgb(hex: string): RGB {
    // Remove '#' if present
    const cleanHex = hex.replace(/^#/, '');

    // Handle 3-character hex
    if (cleanHex.length === 3) {
        const r = parseInt(cleanHex[0] + cleanHex[0], 16);
        const g = parseInt(cleanHex[1] + cleanHex[1], 16);
        const b = parseInt(cleanHex[2] + cleanHex[2], 16);
        if (isNaN(r) || isNaN(g) || isNaN(b)) {
            throw new Error(`Invalid hex color: ${hex}`);
        }
        return { r, g, b };
    }

    // Handle 6-character hex
    if (cleanHex.length === 6) {
        const r = parseInt(cleanHex.substring(0, 2), 16);
        const g = parseInt(cleanHex.substring(2, 4), 16);
        const b = parseInt(cleanHex.substring(4, 6), 16);
        if (isNaN(r) || isNaN(g) || isNaN(b)) {
            throw new Error(`Invalid hex color: ${hex}`);
        }
        return { r, g, b };
    }

    throw new Error(`Invalid hex color: ${hex}`);
}

/**
 * Calculates the average color from an array of RGB colors.
 *
 * @param colors - Array of RGB colors
 * @returns Average RGB color
 */
export function averageColor(colors: RGB[]): RGB {
    if (colors.length === 0) {
        throw new Error('Cannot calculate average of empty array');
    }

    const sum = colors.reduce(
        (acc, color) => ({
            r: acc.r + color.r,
            g: acc.g + color.g,
            b: acc.b + color.b,
        }),
        { r: 0, g: 0, b: 0 },
    );

    return {
        r: Math.round(sum.r / colors.length),
        g: Math.round(sum.g / colors.length),
        b: Math.round(sum.b / colors.length),
    };
}

/**
 * Determines if a contrast ratio meets WCAG 2.0 level AA standards.
 *
 * @param contrastRatio - The contrast ratio to check
 * @param isLargeText - Whether the text is considered large (18pt+ or 14pt+ bold)
 * @returns true if contrast meets AA standards
 */
export function meetsWCAG_AA(contrastRatio: number, isLargeText: boolean = false): boolean {
    return isLargeText ? contrastRatio >= 3 : contrastRatio >= 4.5;
}

/**
 * Determines if a contrast ratio meets WCAG 2.0 level AAA standards.
 *
 * @param contrastRatio - The contrast ratio to check
 * @param isLargeText - Whether the text is considered large (18pt+ or 14pt+ bold)
 * @returns true if contrast meets AAA standards
 */
export function meetsWCAG_AAA(contrastRatio: number, isLargeText: boolean = false): boolean {
    return isLargeText ? contrastRatio >= 4.5 : contrastRatio >= 7;
}

/**
 * Extracts RGB colors from ImageData pixel data.
 *
 * @param imageData - ImageData object from canvas context
 * @param sampleRate - Sample every Nth pixel (default: 1 = sample all pixels)
 * @returns Array of RGB colors
 */
export function extractColorsFromImageData(imageData: ImageData, sampleRate: number = 1): RGB[] {
    const colors: RGB[] = [];
    const data = imageData.data;

    // ImageData.data is a Uint8ClampedArray with [r, g, b, a, r, g, b, a, ...]
    for (let i = 0; i < data.length; i += 4 * sampleRate) {
        colors.push({
            r: data[i],
            g: data[i + 1],
            b: data[i + 2],
        });
    }

    return colors;
}

/**
 * Constants for brightness dimming levels based on scene attributes recommendations.
 * These match the backend constants in web_parsing/scene_attributes.py:
 * - NORMAL_BRIGHTNESS = 1.0
 * - LIGHTLY_DIMMED_BRIGHTNESS = 0.75
 * - DARK_DIMMED_BRIGHTNESS = 0.5
 */
const NORMAL_BRIGHTNESS = 1.0;
const LIGHTLY_DIMMED_BRIGHTNESS = 0.75;
const DARK_DIMMED_BRIGHTNESS = 0.5;

/**
 * Calculates the appropriate media brightness value based on text contrast ratio.
 * This replaces the backend's text legibility detection with a deterministic
 * contrast-based approach using WCAG 2.0 standards.
 *
 * Logic approximates the backend scene_attributes.py behavior:
 * - High contrast (>= 4.5): Text is fully legible → no dimming needed
 * - Medium contrast (3-4.5): Text is somewhat legible → light dimming helps
 * - Low contrast (< 3): Text is not legible → dark dimming required
 *
 * @param contrastRatio - WCAG 2.0 contrast ratio between text and background (1-21)
 * @returns Brightness value (0.5, 0.75, or 1.0)
 */
export function calculateBrightnessFromContrast(
    contrastRatio: number,
    debug: boolean = false,
): number {
    if (debug) {
        // eslint-disable-next-line no-console
        console.log(`contrastRatio: ${contrastRatio}`);
    }
    // Low contrast: text is not legible, apply dark dimming
    if (contrastRatio < 3) {
        return DARK_DIMMED_BRIGHTNESS;
    }

    // Medium contrast: text is somewhat legible, apply light dimming
    // Using WCAG AA threshold for normal text (4.5:1) as the upper bound
    if (contrastRatio < 4.5) {
        return LIGHTLY_DIMMED_BRIGHTNESS;
    }

    // High contrast: text is fully legible, no dimming needed
    return NORMAL_BRIGHTNESS;
}
